import { FC, MouseEvent } from 'react';
import Proptypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Divider,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@material-ui/core';
import { EJobResultType, EJobTitle, Job } from '../../../../types/job';
import { Pagination } from '../../../../components/pagination';
import { ResourceError } from '../../../../components/resource-error';
import { ResourceUnavailable } from '../../../../components/resource-unavailable';
import { Scrollbar } from '../../../../components/scrollbar';
import { EventStatus } from '../../../../components/job/event-status';
import { useAuth } from '../../../../hooks/use-auth';
import {
  getJobDetailUrl,
  getJobRunningStatus,
  getResultStatus,
} from '../../../../utils/jobs-helper';
import { JobsMenu } from '../../../../components/job/jobs-menu';
import { Sort } from '../../../../types/filter';
import { JobsItemCell } from './jobs-item-cell';
import { JobsSkuCell } from './jobs-sku-cell';
import { getIphoneDeviceName } from '../../../../utils/utils';

interface JobsTableProps {
  error: string;
  isLoading: boolean;
  onPageChange: (newPage: number) => void;
  onSortChange: (event: MouseEvent<HTMLElement>, property: string) => void;
  page: number;
  jobs: Job[];
  jobsCount: number;
  sort: Sort;
  sortBy: string;
}

const columns = [
  {
    id: 'id',
    label: 'Event ID',
    sortable: false,
  },
  {
    id: 'item.title',
    label: 'Item',
    sortable: false,
  },
  {
    id: 'item.sku',
    label: 'SKU Title',
    sortable: false,
  },
  {
    id: 'user',
    label: 'User',
  },
  {
    id: 'createdAt',
    label: 'Date Time',
  },
  {
    id: 'status',
    label: 'Status',
    sortable: false,
  },
  {
    id: 'time',
    label: 'Time',
    sortable: false,
  },
  {
    id: 'device',
    label: 'Device',
    sortable: false,
  },
];

const getRowColor = (job: Job) => {
  const status = getResultStatus(job);
  const isRunning = getJobRunningStatus(job);
  if (
    !isRunning &&
    (status === EJobResultType.NOT_VERIFIED ||
      (status === EJobResultType.FAILED &&
        (job.title === EJobTitle.PROTECTION || job.title === EJobTitle.REGISTRATION)))
  ) {
    return '#FFE5E7';
  }
  return 'unset';
};

export const JobsTable: FC<JobsTableProps> = (props) => {
  const { error, isLoading, onPageChange, onSortChange, page, jobs, jobsCount, sort, sortBy } =
    props;

  const { tenant } = useAuth();

  const displayLoading = isLoading;
  const displayError = Boolean(!isLoading && error);
  const displayUnavailable = Boolean(!isLoading && !error && !jobs.length);

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}
    >
      <Scrollbar>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id}>
                  <TableSortLabel
                    active={sortBy === column.id}
                    direction={sortBy === column.id ? sort : 'asc'}
                    disabled={isLoading || column.sortable === false}
                    onClick={(event) => onSortChange(event, column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow
                hover
                key={job.id}
                sx={{
                  backgroundColor: getRowColor(job),
                }}
              >
                {/* Event ID */}
                <TableCell>
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                    }}
                  >
                    <Link
                      color="inherit"
                      component={RouterLink}
                      sx={{ display: 'block' }}
                      to={getJobDetailUrl(job, tenant)}
                      variant="subtitle2"
                    >
                      <Typography
                        variant="subtitle1"
                        noWrap
                        textOverflow="ellipsis"
                        maxWidth={{
                          xs: 100,
                          xl: 345,
                        }}
                        sx={{
                          direction: 'rtl',
                        }}
                        title={job.id}
                      >
                        {job.id}
                      </Typography>
                    </Link>
                  </Box>
                </TableCell>

                {/* Item Title + Item UID */}
                <TableCell>
                  <JobsItemCell job={job} />
                </TableCell>

                {/* SKU */}
                <TableCell>
                  <JobsSkuCell job={job} />
                </TableCell>

                {/* User */}
                <TableCell>{job.user && <Box>{job.user.email}</Box>}</TableCell>

                {/* Date Time */}
                <TableCell>
                  <Box>
                    <Typography color="inherit" variant="body2">
                      {format(new Date(job.startedAt || job.createdAt), 'h:mmaaa MM/dd/yyyy')}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <EventStatus event={job} />
                </TableCell>

                {/* Time */}
                <TableCell align="right">
                  <Box>
                    <Typography color="inherit" variant="body2">
                      {Number.isFinite(job.time) ? `${(job.time / 1000).toFixed(2)} s` : '-'}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Device Model */}
                <TableCell>
                  <Box>
                    <Typography color="inherit" variant="body2">
                      {(() => {
                        const model =
                          job.publicMetadata?.deviceInfo?.model ||
                          job.publicMetadata?.data?.deviceInfo?.model ||
                          job.publicMetadata?.model ||
                          job.publicMetadata?.data?.model ||
                          job.publicMetadata?.blurDebugJson?.device?.model;
                        return model ? getIphoneDeviceName(model) : '-';
                      })()}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Menu */}
                <TableCell align="right" sx={{ pl: 0 }}>
                  <JobsMenu job={job} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
      {displayLoading && (
        <Box sx={{ p: 2 }}>
          <Skeleton height={42} />
          <Skeleton height={42} />
          <Skeleton height={42} />
        </Box>
      )}
      {displayError && (
        <ResourceError
          error={error}
          sx={{
            flexGrow: 1,
            m: 2,
          }}
        />
      )}
      {displayUnavailable && (
        <ResourceUnavailable
          text="No activities in selected time period."
          sx={{
            flexGrow: 1,
            m: 2,
          }}
        />
      )}
      <Divider sx={{ mt: 'auto' }} />
      <Pagination
        disabled={isLoading}
        onPageChange={onPageChange}
        page={page}
        rowsCount={jobsCount}
      />
    </Box>
  );
};

JobsTable.defaultProps = {
  page: 1,
  jobs: [],
  jobsCount: 0,
  sort: 'desc',
  sortBy: 'createdAt',
};

JobsTable.propTypes = {
  error: Proptypes.string,
  isLoading: Proptypes.bool,
  onPageChange: Proptypes.func,
  onSortChange: Proptypes.func,
  page: Proptypes.number,
  jobs: Proptypes.array,
  jobsCount: Proptypes.number,
  sort: Proptypes.oneOf(['asc', 'desc']),
  sortBy: Proptypes.string,
};
